// Sub-componente: Anexos da parcela. Upload (pre-signed S3 PUT),
// download e remocao via fin_*_anexo_parcela actions.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtData } from './utils';

export function AnexosParcela({ parcelaId }: { parcelaId: string }) {
  const [anexos, setAnexos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tipoUp, setTipoUp] = useState<'nf' | 'comprovante' | 'bm_assinado' | 'outro'>('nf');

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_anexos_parcela', parcela_id: parcelaId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAnexos(d.anexos || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [parcelaId]);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const token = await getToken();
      // 1) Pre-signed URL
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_upload_url_anexo_parcela',
        parcela_id: parcelaId, filename: file.name, content_type: file.type,
      }, { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      // 2) PUT direto S3
      await fetch(d1.upload_url, { method: 'PUT', body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      // 3) Registra metadata
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_criar_anexo_parcela',
        parcela_id: parcelaId, nome: file.name, s3_key: d1.s3_key,
        tipo: tipoUp, tamanho_bytes: file.size, mime: file.type,
      }, { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      alert('Erro upload: ' + (e.response?.data?.error || e.message));
    } finally { setUploading(false); }
  }

  async function baixar(a: any) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_download_url_anexo_parcela', id: a.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      window.open(d.download_url, '_blank');
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function remover(a: any) {
    if (!window.confirm(`Remover anexo "${a.nome}"?`)) return;
    const token = await getToken();
    await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'fin_excluir_anexo_parcela', id: a.id },
      { headers: { Authorization: token } });
    await carregar();
  }

  const fmtTamanho = (b: number | null) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };
  const tipoLabel: Record<string, { label: string; emoji: string; cor: string; bg: string }> = {
    nf:           { label: 'Nota Fiscal',     emoji: '🧾', cor: '#0930D0', bg: '#EFF3FF' },
    comprovante:  { label: 'Comprovante',     emoji: '💸', cor: '#0a7f4a', bg: '#E8F5EE' },
    bm_assinado:  { label: 'BM Assinado',     emoji: '✍️', cor: '#7C3AED', bg: '#F3E8FF' },
    outro:        { label: 'Outro',           emoji: '📎', cor: '#555',    bg: '#F2F2F4' },
  };

  return (
    <div>
      {/* Upload area */}
      <div style={{ padding: 14, background: '#FAFBFF',
        border: '0.5px dashed #d8dcec', borderRadius: 6, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#020D7F', fontWeight: 600 }}>
            Tipo do anexo:
          </span>
          <select value={tipoUp} onChange={e => setTipoUp(e.target.value as any)}
            style={{ padding: '6px 10px', fontSize: 12, border: '0.5px solid #ddd',
              borderRadius: 4 }}>
            <option value="nf">🧾 Nota Fiscal</option>
            <option value="comprovante">💸 Comprovante pagamento</option>
            <option value="bm_assinado">✍️ BM Assinado</option>
            <option value="outro">📎 Outro</option>
          </select>
          <label style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: '#0930D0', color: '#fff', borderRadius: 4,
            cursor: uploading ? 'wait' : 'pointer' }}>
            {uploading ? 'Enviando…' : '+ Anexar arquivo'}
            <input type="file" hidden disabled={uploading}
              accept=".pdf,.jpg,.jpeg,.png,.xml,.xlsx,.docx"
              onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); }} />
          </label>
        </div>
        <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
          PDF, imagens, XML, XLSX, DOCX. Máx 10 MB.
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 20, color: '#888', textAlign: 'center' }}>Carregando…</div>
      ) : anexos.length === 0 ? (
        <div style={{ padding: 30, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>
          Nenhum anexo. Suba a NF aqui pra ter tudo num lugar só.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {anexos.map(a => {
            const tl = tipoLabel[a.tipo] || tipoLabel.outro;
            return (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: 10, borderRadius: 5, background: '#fff', border: '0.5px solid #e0e0e0' }}>
                <span style={{ fontSize: 18 }}>{tl.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#222',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.nome}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                    <span style={{ background: tl.bg, color: tl.cor, padding: '1px 6px',
                      borderRadius: 8, fontWeight: 600, marginRight: 6 }}>{tl.label}</span>
                    {fmtTamanho(a.tamanho_bytes)} · {fmtData(a.criado_em)}
                    {a.criado_por && ` · ${a.criado_por.split('@')[0]}`}
                  </div>
                </div>
                <button onClick={() => baixar(a)}
                  style={{ padding: '5px 10px', fontSize: 11, background: '#fff',
                    color: '#0930D0', border: '0.5px solid #0930D0',
                    borderRadius: 3, cursor: 'pointer' }}>
                  ⬇ Baixar
                </button>
                <button onClick={() => remover(a)}
                  style={{ padding: '5px 10px', fontSize: 11, background: 'transparent',
                    color: '#B91626', border: '0.5px solid #FBBABA',
                    borderRadius: 3, cursor: 'pointer' }}>
                  Excluir
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
